import find from 'lodash/find';

import { typhonAjaxFetch } from '~/helpers/api';
import { typhonRoutes } from '~/helpers/routes';

export const updateFiles = (defaultFileType, uploaded, setFiles) => {
  setFiles(prevState => [...prevState,
    ...[...uploaded].reduce((acc, file) => {
      const used = prevState.filter(p => p.fileObj.name === file.name);
      if (used.length) { return acc; }
      acc.push({
        fileObj: file,
        type: defaultFileType && defaultFileType.document_type_id,
        notes: '',
      });
      return acc;
    }, []),
  ]);
};

export const handleFileChange = (defaultFileType, event, setFiles) => {
  const uploaded = event.target.files;
  updateFiles(defaultFileType, uploaded, setFiles);
};

export const handleUploadError = (message, setUploading, toggleModal, addSnackBar) => {
  setUploading(false);
  toggleModal(false);
  addSnackBar({ title: message, type: 'danger' });
};

export const handleUploadDocument = async ({
  event,
  typhonDomain,
  setUploading,
  documentTypes,
  files,
  onClose,
  successHandler,
  agentEmail,
  caseMetaData,
  entity,
  evaluationToken,
  investigationToken,
  addSnackBar,
}) => {
  event.preventDefault();
  event.stopPropagation();
  setUploading(true);

  // Timestamp to be shared between documents to group them on the timeline
  const currentTimestamp = new Date().toISOString().replace('T', ' ').replace('Z', '');

  const messages = [];
  const requests = files.map(async file => {
    const selectedDocType = find(documentTypes, { document_type_id: file.type });
    const fileSplits = file.fileObj.name.split('.');
    const docName = fileSplits.slice(0, (fileSplits.length - 1)).join('.');
    const docExt = fileSplits.pop().toLowerCase();
    const docMetadata = {
      name: docName,
      extension: docExt,
      type: selectedDocType.type,
      timestamp: currentTimestamp,
      ...(file.notes && {
        note: file.notes,
        note_author_agent_email: agentEmail,
      }),
      ...(investigationToken && {
        investigation_token: investigationToken,
      }),
      ...(evaluationToken && {
        evaluation_token: evaluationToken,
      }),
      ...(caseMetaData || {}),
    };
    try {
      // upload document metadata
      const uploadDocMeta = await typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.documentUpload}`,
        {
          method: 'POST',
          ...(entity
            && entity.entity_token
            && { headers: { 'Alloy-Entity-Token': entity.entity_token } }
          ),
          body: JSON.stringify(docMetadata),
        },
      );
      if (uploadDocMeta.result === 'error') {
        messages.push({
          docName,
          message: uploadDocMeta.message,
          type: 'danger',
        });
        return false;
      }

      // upload actual file
      const uploadDocument = await typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.documentUpload}/${uploadDocMeta.document_token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': file.fileObj.type },
          body: file.fileObj,
        },
      );
      if (uploadDocument.result === 'error') {
        messages.push({
          docName,
          message: uploadDocument.message,
          type: 'danger',
        });
        return false;
      }
      messages.push({
        docName,
        message: `${docName} uploaded successfully`,
        type: 'success',
      });
      return uploadDocMeta.document_token;
    } catch (err) {
      messages.push({
        docName,
        message: `${err}`,
        type: 'danger',
      });
      return false;
    }
  });

  Promise.all(requests)
    .then(valuesOrErrors => {
      setUploading(false);
      onClose();
      const hasErrors = Boolean(valuesOrErrors.filter(e => !e).length);
      if (!hasErrors) {
        successHandler(valuesOrErrors);
        return;
      }

      // with errors, should show feedback for each failed/succeded upload.
      messages.forEach(each => {
        const { docName, message: errorMessage, type } = each;
        const snack = { title: `${docName} uploaded successfully`, type };
        if (type === 'danger') {
          snack.title = `Failed to upload ${docName}`;
          snack.body = errorMessage;
        }
        addSnackBar(snack);
      });
    });
};

export const formatFileSize = bytes => {
  if (bytes > 1000000) {
    return `${(bytes / 1000000).toFixed(1)}MB`;
  }
  return `${(bytes / 1000).toFixed(1)}KB`;
};
